import {
  faArrowsSpin,
  faArrowUpShortWide,
  faBoxArchive,
  faDownLeftAndUpRightToCenter,
  faLocationDot,
  faObjectGroup,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  faBoxOpen,
  faCoins,
  faExternalLink,
  faInfoCircle,
  faPrint,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { OverlayTrigger, Spinner, Tooltip as BSTooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import BatchPowderQualityStatusIcon from 'src/components/BatchPowderQualityStatusIcon';
import ConvertedUnits from 'src/components/ConvertedUnits';
import Loader from 'src/components/loader';
import MaterialLotCardDetails from 'src/components/MaterialLotCardDetails';
import PermanentContainerCardDetails from 'src/components/PermanentContainerCardDetails';
import PostProcessorCard from 'src/components/PostProcessorCard';
import PrinterCard from 'src/components/PrinterCard';
import StatusColorCode from 'src/components/status-color-dot';
import Tooltip from 'src/components/Tooltip';
import TruncatedItemsList from 'src/components/TruncatedItemsList';
import TruncatedTitle from 'src/components/TruncatedTitle';
import {
  BATCH_CARD_TABS,
  LOCALSTORAGE_KEYS,
  MATERIAL_BATCH_STATUS_VERBOSE,
  STATUS_COLOR_CODE_TYPES,
} from 'src/utils/constants';
import routes from 'src/utils/routes';
import { getRouteURI, getShortUuid } from 'src/utils/url';
import useLocalStorage from 'src/utils/useLocalStorage';

export const BatchCardExpanded = ({
  batch,
  hideContainers,
  togglePreview,
  subLocation,
  cardState,
  allContainers,
  containersFetching,
  containersFetchError,
  allLocationsByUri,
  allSubLocationsByUri,
  materialLots,
  shouldShowBatchLink,
  isMobileDevice,
  loadedPrinter,
  loadedPostProcessor,
  mlineLps,
  batchHasIncompleteTransactions,
  batchTransactionsLoading,
}) => {
  const [debugMode] = useLocalStorage(LOCALSTORAGE_KEYS.DEBUG_MODE);
  const [activeTab, setActiveTab] = useState(
    hideContainers ? BATCH_CARD_TABS.LOTS : BATCH_CARD_TABS.CONTAINERS
  );
  const [locationShown, setLocationShown] = useState(false);
  const batchMaterialNames = _uniq(_map(batch.materials, 'name'));

  const activeIndex = 0;
  const slickRef = useRef(null);

  const lotsFetching = cardState[BATCH_CARD_TABS.LOTS].fetching;
  const locationsFetching = _isEmpty(allLocationsByUri);

  const handleSwitchTab = tab => setActiveTab(tab);
  const toggleLocation = () => setLocationShown(previous => !previous);

  const renderLotsTab = () => {
    if (lotsFetching && !materialLots.length) {
      return <Loader showText={false} />;
    }

    if (!materialLots.length) {
      return (
        <p className='no-card-resources'>
          <FontAwesomeIcon icon={faInfoCircle} className='d-block spacer-right batch-info-color' />
          Material Lots not found.
        </p>
      );
    }

    return materialLots.map(lot => (
      <MaterialLotCardDetails
        key={lot.uri}
        lot={lot}
        materials={batch.materials}
        debugMode={debugMode}
        location={allLocationsByUri[lot.location]}
        subLocation={allSubLocationsByUri[lot.sub_location]}
        state={cardState[BATCH_CARD_TABS.LOTS]}
        customMainStyle={{ margin: '20px 25px' }}
        dataCy={`batch-card-lot-${getShortUuid(lot.uri)}`}
      />
    ));
  };

  const renderContainersTab = () => {
    if (hideContainers) return null;
    if (containersFetching && !allContainers.length) {
      return <Loader showText={false} />;
    }

    if (loadedPostProcessor) {
      return (
        <PostProcessorCard
          postProcessor={loadedPostProcessor}
          location={allLocationsByUri[loadedPostProcessor.location]}
          subLocation={allSubLocationsByUri[loadedPostProcessor.sub_location]}
          customMainStyle={{ margin: '10px 25px' }}
        />
      );
    }

    if (!allContainers.length || !_isEmpty(mlineLps)) {
      if (loadedPrinter) {
        return (
          <PrinterCard
            printer={loadedPrinter}
            mlineLps={mlineLps}
            batch={batch}
            subLocation={subLocation}
            customMainStyle={{ margin: '10px 25px' }}
          />
        );
      }
      return (
        <p className='no-card-resources'>
          <OverlayTrigger
            placement='top'
            overlay={
              <BSTooltip>
                <p>
                  Please use the &quot;Load&quot; batch action to containerise it from the QR app or
                  &quot;Containerise Material&quot; from the batch page to containerise it from the
                  Flows desktop app.
                </p>
              </BSTooltip>
            }
          >
            <span className='spacer-right'>
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </OverlayTrigger>
          This material batch doesn&apos;t have any containers.
        </p>
      );
    }

    return allContainers.map(container => (
      <PermanentContainerCardDetails
        key={container.uri}
        swapRightIconPosition
        hideContainerStatus
        units={batch.units}
        permanentContainer={container}
        locationsFetching={locationsFetching}
        isError={containersFetchError}
        location={allLocationsByUri[container.location]?.name}
        subLocation={allSubLocationsByUri[container.sub_location]?.name}
        customMainStyle={{ margin: '20px 25px', boxShadow: '0 0 4px 2px rgba(0, 0, 0, 0.08)' }}
        dataCy={`batch-card-container-${getShortUuid(container.uri)}`}
      />
    ));
  };

  const renderBatchTransactionsTooltip = () => {
    if (batchTransactionsLoading)
      return (
        <Spinner
          animation='border'
          size='sm'
          variant='light'
          className='spacer-left mb0 align-self-center'
        />
      );

    if (!batchHasIncompleteTransactions) return null;

    return (
      <div className='spacer-left'>
        <Tooltip
          id='incomplete_transactions_tooltip'
          placement='bottom'
          trigger={
            <span className='powder_quality_badge'>
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                color='#FF9903'
                className='spacer-right'
              />
              <span className='fs-mobile-10'>Pending</span>
            </span>
          }
        >
          <div>Batch has pending transactions</div>
        </Tooltip>
      </div>
    );
  };

  const renderTabTitle = () => {
    if (loadedPrinter) {
      if (!_isEmpty(mlineLps)) {
        return 'LPS';
      }
      return 'Printer';
    }

    if (loadedPostProcessor) {
      return 'MHS';
    }

    return 'Containers';
  };

  return (
    <div className='batch-card-expanded' data-cy='batch-card'>
      <div className='batch-card-expanded-header'>
        <div className='batch-card-expanded-header-main'>
          <div
            className='batch-card-expanded-header-main-details'
            style={isMobileDevice ? { marginBottom: '15px' } : {}}
          >
            <div className='text-left batch-name'>
              <p className='batch-card-expanded-header-main-batch-title'>
                <FontAwesomeIcon icon={faBoxArchive} className='spacer-right' />
                Batch
              </p>
              {shouldShowBatchLink ? (
                <Link
                  className='batch-card-main-details-main-link'
                  to={getRouteURI(routes.materialContainer, {}, { batch: batch.uuid })}
                  data-cy='batch-card-batch-link'
                >
                  <FontAwesomeIcon icon={faExternalLink} className='spacer-right' />
                  <p className='mb0' data-cy='batch-card-batch-id'>
                    {getShortUuid(batch.uuid)}
                  </p>
                </Link>
              ) : (
                <p className='mb0 batch-card-main-details-main-link' data-cy='batch-card-batch-id'>
                  {getShortUuid(batch.uuid)}
                </p>
              )}
            </div>

            {!isMobileDevice && (
              <div className='batch-card-expanded-header-location'>
                <FontAwesomeIcon icon={faLocationDot} className='mr-1 text-white mt5' />
                <div className='batch-card-expanded-header-location-details'>
                  <TruncatedTitle
                    title={batch.location_name}
                    titleClassName='mb0 font-medium font-size-14'
                    maxTextLength={25}
                    maxTextLengthMobile={20}
                    dataCy='batch-card-location'
                  />
                  <TruncatedTitle
                    title={subLocation}
                    titleClassName='mb0 font-light font-size-12'
                    maxTextLength={35}
                    maxTextLengthMobile={30}
                    dataCy='batch-card-sub-location'
                  />
                </div>
              </div>
            )}
          </div>
          <div className='d-flex flex-column align-items-start'>
            <div className='batch-card-expanded-header-status-details align-items-center'>
              <div className='d-flex align-items-center'>
                <p className='mb0 d-flex align-items-center'>
                  <StatusColorCode
                    status={batch.status}
                    type={STATUS_COLOR_CODE_TYPES.BATCH_STATUS}
                    sizeClassName='small'
                    dataCy='batch-card-status-dot'
                  />
                </p>
                <p className='mb0 spacer-left text-white' data-cy='batch-card-status'>
                  {MATERIAL_BATCH_STATUS_VERBOSE[batch.status]}
                </p>
              </div>
              {batch.powder_quality && (
                <div className='spacer-left'>
                  <BatchPowderQualityStatusIcon
                    qualityStatus={batch.powder_quality}
                    dataCy='batch-card-powder-quality'
                  />
                </div>
              )}
              {renderBatchTransactionsTooltip()}
            </div>
            {isMobileDevice && (
              <div className='mt5 text-left'>
                <div className='cursor-pointer'>
                  <p
                    className='mb0 badge badge-secondary batch-card-expanded-location-mobile-btn'
                    data-cy='batch-card-location-toggle-btn'
                    onClick={toggleLocation}
                  >
                    {!locationShown ? 'Show' : 'Hide'} Location
                  </p>
                </div>
                {locationShown ? (
                  <div className='batch-card-expanded-location-mobile'>
                    <div>
                      <div className='d-flex align-items-center'>
                        <FontAwesomeIcon className='spacer-right' icon={faLocationDot} />
                        <TruncatedTitle
                          title={batch.location_name}
                          titleClassName='mb0 font-medium font-size-14'
                          maxTextLength={25}
                          maxTextLengthMobile={50}
                          dataCy='batch-card-location'
                        />
                      </div>
                      <p className='badge batch-card-expanded-sub-location-mobile'>
                        <TruncatedTitle
                          title={subLocation}
                          titleClassName='mb0 font-light font-size-12'
                          maxTextLength={35}
                          maxTextLengthMobile={60}
                          dataCy='batch-card-sub-location'
                        />
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
        <div className='batch-card-expanded-header-secondary'>
          <p className='mb10 batch-card-expanded-header-secondary-details'>
            <ConvertedUnits
              quantity={batch.quantity}
              units={batch.units}
              dataCy='batch-card-quantity'
            />
            <div className='batch-card-header-secondary-icon'>
              <FontAwesomeIcon className='spacer-left' icon={faArrowUpShortWide} />
            </div>
          </p>
          <p
            className='mt10 mb10 batch-card-expanded-header-secondary-details'
            data-cy='batch-card-usage-cycles'
          >
            {batch.usage_cycles}
            <div className='batch-card-header-secondary-icon'>
              <FontAwesomeIcon className='spacer-left' icon={faArrowsSpin} />
            </div>
          </p>
          <p className='mb0 batch-card-expanded-header-secondary-details'>
            <TruncatedItemsList
              data={batchMaterialNames}
              showCount={1}
              icon={faObjectGroup}
              showVisibleIcon={false}
              dataCy='batch-card-materials'
            />
            <div className='batch-card-header-secondary-icon'>
              <FontAwesomeIcon className='spacer-left' icon={faObjectGroup} />
            </div>
          </p>
        </div>
      </div>
      <div className='batch-card-expanded-body'>
        <div className='batch-card-expanded-body-tabs'>
          {!hideContainers && (
            <button
              type='button'
              className={`btn batch-card-expanded-body-tab
            ${activeTab === BATCH_CARD_TABS.CONTAINERS ? 'batch-card-expanded-body-tab-active' : ''}`}
              data-cy='batch-card-containers-tab'
              onClick={() => handleSwitchTab(BATCH_CARD_TABS.CONTAINERS)}
            >
              <FontAwesomeIcon
                icon={loadedPrinter || loadedPostProcessor ? faPrint : faCoins}
                className='spacer-right'
              />
              {renderTabTitle()}
            </button>
          )}
          <button
            type='button'
            className={`btn batch-card-expanded-body-tab
            ${activeTab === BATCH_CARD_TABS.LOTS ? 'batch-card-expanded-body-tab-active' : ''}`}
            data-cy='batch-card-lots-tab'
            onClick={() => handleSwitchTab(BATCH_CARD_TABS.LOTS)}
          >
            <FontAwesomeIcon icon={faBoxOpen} className='spacer-right' />
            Lots
          </button>
        </div>

        <Slider
          ref={slickRef}
          swipe
          dots
          initialSlide={activeIndex}
          infinite={false}
          speed={200}
          className='batch-content-slick-container'
        >
          {activeTab === BATCH_CARD_TABS.CONTAINERS && renderContainersTab()}
          {activeTab === BATCH_CARD_TABS.LOTS && renderLotsTab()}
        </Slider>
        <button
          type='button'
          className='btn btn-link btn-sm text-white batch-card-expanded-toggle'
          data-cy='batch-card-collapse-btn'
          onClick={togglePreview}
        >
          <FontAwesomeIcon icon={faDownLeftAndUpRightToCenter} />
        </button>
      </div>
    </div>
  );
};

BatchCardExpanded.defaultProps = {
  shouldShowBatchLink: true,
  loadedPrinter: null,
  loadedPostProcessor: null,
  mlineLps: null,
  hideContainers: false,
  batchHasIncompleteTransactions: false,
  batchTransactionsLoading: false,
};

BatchCardExpanded.propTypes = {
  /* eslint-disable camelcase */
  batch: PropTypes.shape({
    uuid: PropTypes.string,
    units: PropTypes.string,
    status: PropTypes.string,
    quantity: PropTypes.number,
    powder_quality: PropTypes.string,
    usage_cycles: PropTypes.number,
    location_name: PropTypes.string,
    materials: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ),
  }).isRequired,
  subLocation: PropTypes.string.isRequired,
  togglePreview: PropTypes.func.isRequired,
  cardState: PropTypes.shape({
    [BATCH_CARD_TABS.LOTS]: PropTypes.shape({
      fetching: PropTypes.bool,
      error: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  allContainers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allLocationsByUri: PropTypes.shape({}).isRequired,
  allSubLocationsByUri: PropTypes.shape({}).isRequired,
  materialLots: PropTypes.arrayOf(
    PropTypes.shape({
      location: PropTypes.string,
      sub_location: PropTypes.string,
    })
  ).isRequired,
  shouldShowBatchLink: PropTypes.bool,
  isMobileDevice: PropTypes.bool.isRequired,
  loadedPrinter: PropTypes.shape({}),
  loadedPostProcessor: PropTypes.shape({
    location: PropTypes.string,
    sub_location: PropTypes.string,
  }),
  mlineLps: PropTypes.oneOfType([PropTypes.shape({}), undefined]),
  containersFetching: PropTypes.bool.isRequired,
  containersFetchError: PropTypes.bool.isRequired,
  hideContainers: PropTypes.bool,
  batchHasIncompleteTransactions: PropTypes.bool,
  batchTransactionsLoading: PropTypes.bool,
};
